<template>
  <div class="text-right">
    <!-- Custom selection chip (visible only after user confirms a custom range) -->
    <v-chip
      v-if="customLabel"
      class="pa-2"
      color="grey lighten-2"
      close
      @click:close="cancelCustomDate"
    >
      <v-avatar>
        <v-icon small>mdi-calendar-multiple-check</v-icon>
      </v-avatar>
      <span>
        {{ startDateDisplayNormal }}
        <template v-if="range">
          {{ $t("labels.to") }} {{ endDateDisplayNormal }}
        </template>
      </span>
    </v-chip>

    <!-- “just custom date” mode -->
    <template v-if="justCustomDate && !customLabel">
      <v-chip class="pa-2" color="white" @click="customBox">
        <v-avatar>
          <v-icon small>mdi-calendar-multiple-check</v-icon>
        </v-avatar>
        <span>pick a date</span>
      </v-chip>
    </template>

    <!-- Preset selector (hidden when custom chip is shown) -->
    <template v-else>
      <div v-if="showSelectedDate" class="text-right mb-1">
        {{ start.date | moment("MM/DD/YYYY") }} -
        {{ end.date | moment("MM/DD/YYYY") }}
      </div>

      <v-select
        v-if="!customLabel"
        hide-details
        item-text="name"
        :items="items"
        return-object
        v-model="selected"
        outlined
        ref="selectDropdown"
      >
        <template v-if="!historyLimitedAccess" v-slot:prepend-item>
          <v-list-item ripple @click="customBox">
            <v-list-item-action>
              <v-icon>mdi-calendar-multiple-check</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{
                $t("pageHeader.customDate")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
        </template>

        <template v-slot:item="{ item }">
          <span>{{ item.name }}</span>
        </template>

        <template v-if="!historyLimitedAccess" v-slot:append-item>
          <v-divider class="mt-2" />
          <v-list-item ripple @click="customBox">
            <v-list-item-action>
              <v-icon>mdi-calendar-multiple-check</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{
                $t("pageHeader.customDate")
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
    </template>

    <!-- Custom date dialog -->
    <v-dialog v-model="custom" width="500" persistent>
      <v-card>
        <CardToolbar
          :title="$t('pageHeader.customDate')"
          :isModal="true"
          @cancel="cancel"
        />
        <v-divider />

        <v-container>
          <v-row justify="center" align="stretch" dense>
            <!-- START -->
            <v-col cols="12" sm="6">
              <span>{{ $t("labels.from") }}</span>
              <v-menu
                ref="startMenu"
                v-model="startMenu"
                :close-on-content-click="false"
                :return-value.sync="startDateDisplay"
                transition="scale-transition"
                offset-y
                max-width="320"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details
                    v-model="startDateDisplayNormal"
                    :label="range ? 'Start Date' : label ? label : 'Date'"
                    readonly
                    :clearable="clearable"
                  />
                </template>
                <v-card>
                  <v-tabs v-model="startActive" right slider-color="black">
                    <v-tab ripple><v-icon>mdi-calendar</v-icon></v-tab>
                    <v-tab v-if="!hideTime" ripple
                      ><v-icon>mdi-clock-outline</v-icon></v-tab
                    >

                    <v-tabs-items>
                      <v-tab-item>
                        <v-date-picker
                          color="primary"
                          class="tile elevation-0"
                          v-model="startDate"
                          scrollable
                          :max="endDate"
                        />
                      </v-tab-item>

                      <v-tab-item v-if="!hideTime">
                        <v-time-picker
                          v-model="start.time"
                          color="primary"
                          class="tile elevation-0"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-tabs>

                  <v-divider />
                  <v-card-actions>
                    <v-spacer />
                    <v-btn
                      text
                      color="primary"
                      @click.stop.prevent="startMenu = false"
                      >Cancel</v-btn
                    >
                    <v-btn
                      color="primary"
                      @click.stop.prevent="
                        $refs.startMenu.save(startDateDisplay)
                      "
                      >OK</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>

            <!-- END -->
            <v-col cols="12" sm="6" v-if="range">
              <span>{{ $t("labels.to") }}</span>
              <v-menu
                ref="endMenu"
                v-model="endMenu"
                :close-on-content-click="false"
                :return-value.sync="endDateDisplay"
                transition="scale-transition"
                offset-y
                max-width="320"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details
                    v-model="endDateDisplayNormal"
                    :label="endlabel ? endlabel : 'End Date'"
                    readonly
                    :clearable="clearable"
                  />
                </template>
                <v-card>
                  <v-tabs v-model="endActive" right slider-color="black">
                    <v-tab ripple><v-icon>mdi-calendar</v-icon></v-tab>
                    <v-tab v-if="!hideTime" ripple
                      ><v-icon>mdi-clock-outline</v-icon></v-tab
                    >

                    <v-tab-item>
                      <v-date-picker
                        class="tile elevation-0"
                        v-model="endDate"
                        scrollable
                        :min="startDate"
                        :max="noFuture ? $moment().format('YYYY-MM-DD') : null"
                      />
                    </v-tab-item>

                    <v-tab-item v-if="!hideTime">
                      <v-time-picker
                        class="tile elevation-0"
                        v-model="end.time"
                      />
                    </v-tab-item>
                  </v-tabs>

                  <v-divider />
                  <v-card-actions>
                    <v-spacer />
                    <v-btn
                      text
                      color="primary"
                      @click.stop.prevent="endMenu = false"
                      >Cancel</v-btn
                    >
                    <v-btn
                      color="primary"
                      @click.stop.prevent="$refs.endMenu.save(endDateDisplay)"
                      >OK</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="my-2" />

          <v-row justify="center" align="stretch" dense>
            <v-col cols="12" sm="6">
              <span>{{ $t("labels.weeks") }}</span>
              <v-select hide-details outlined :items="weeks">
                <template v-slot:item="{ item, index }">
                  <v-list-item
                    :key="index"
                    ripple
                    @click.prevent.stop="customDate(item)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.start | moment("MM/DD") }} -
                        {{ item.end | moment("MM/DD") }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <span>{{ $t("labels.days") }}</span>
              <v-select hide-details outlined :items="days">
                <template v-slot:item="{ item, index }">
                  <v-list-item
                    :key="index"
                    ripple
                    @click.prevent.stop="customDate(item)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{
                        item.start | moment("dddd")
                      }}</v-list-item-title>
                      <v-list-item-subtitle>{{
                        item.start | moment("MM/DD/YYYY")
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-container>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            class="v-btn-with-radius"
            @click.stop.prevent="submitDate"
            :disabled="!startDate"
          >
            {{ $t("buttons.go") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "date-time-picker",
  props: [
    "showSelectedDate",
    "label",
    "endlabel",
    "range",
    "hideTime",
    "showIcon",
    "showEndIcon",
    "clearable",
    "initStartDate",
    "initEndDate",
    "noFuture",
    "autoSubmit",
    "defaultRange",
    "justCustomDate",
    "reportView",
  ],
  data() {
    return {
      selectedWeek: null,
      selectedDay: null,
      weeks: [],
      days: [],
      startActive: 0,
      endActive: 0,
      customLabel: false, // controls chip visibility
      custom: false, // dialog
      selected: {
        id: "today",
        name: "Today",
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      startMenu: false,
      day: 1,
      start: { date: null, time: null, selectTime: false },
      endMenu: false,
      historyLimitedAccess: false,
      Integration: null,
      end: { date: null, time: null, selectTime: false },
    };
  },
  async created() {
    // await this.getDayForHistory()
    this.loadWeeks();
    this.loadDays();
  },
  mounted() {
    if (this.defaultRange !== "undefined") {
      const range = this.items.filter(
        (element) => element.id === this.defaultRange
      );
      this.selected = range.length > 0 ? range[0] : this.items[0];
      this.customLabel = false; // preset -> no chip
    } else if (this.reportView) {
      // report view uses explicit dates -> treat as custom
      this.startDate = this.initStartDate;
      this.endDate = this.initEndDate;
      this.customLabel = true;
    }

    if (this.initStartDate && !this.reportView) {
      this.start.date = this.initStartDate;
      this.start.time = !this.hideTime
        ? this.$moment(this.initStartDate).format("HH:mm")
        : null;
    }
    if (this.initEndDate && !this.reportView) {
      this.end.date = this.initEndDate;
      this.end.time = !this.hideTime
        ? this.$moment(this.initEndDate).format("HH:mm")
        : null;
    }
  },
  computed: {
    // ...mapGetters('global', [ 'currentEmployee']),
    ...mapGetters("integration", ["modules"]),

    today() {
      return {
        id: "today",
        name: "Today",
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      };
    },
    yesterday() {
      return {
        id: "yesterday",
        name: "Yesterday",
        start: this.$moment().subtract(1, "days").startOf("days").toISOString(),
        end: this.$moment().subtract(1, "days").endOf("day").toISOString(),
      };
    },
    items() {
      const list = [];
      if (this.historyLimitedAccess) {
        if (this.day > 0) list.push(this.today);
        if (this.day > 1) list.push(this.yesterday);
      } else {
        list.push(this.today, this.yesterday);
      }

      const setting = this.modules.terminalregister
        ? this.modules.terminalregister.IntegrationSettings.metadata.weekStartOf
        : null;

      const weekStartSunday = setting && setting === "Sunday";
      const wkStart = weekStartSunday ? "week" : "isoWeek";

      const addWeekRanges = (limitDays) => {
        if (this.day >= 7 || !this.historyLimitedAccess) {
          list.push(
            {
              id: "thisWeek",
              name: "This Week",
              start: this.$moment().startOf(wkStart).toISOString(),
              end: this.$moment().endOf(wkStart).toISOString(),
            },
            {
              id: "thisWeekToDate",
              name: "This Week To Date",
              start: this.$moment().startOf(wkStart).toISOString(),
              end: this.$moment().endOf("day").toISOString(),
            }
          );
        }
        if (this.day >= 14 || !this.historyLimitedAccess) {
          list.push(
            {
              id: "lastWeek",
              name: "Last Week",
              start: this.$moment()
                .subtract(1, "week")
                .startOf(wkStart)
                .toISOString(),
              end: this.$moment()
                .subtract(1, "week")
                .endOf(wkStart)
                .toISOString(),
            },
            {
              id: "lastWeekToDate",
              name: "Last Week To Date",
              start: this.$moment()
                .subtract(1, "week")
                .startOf(wkStart)
                .toISOString(),
              end: this.$moment().endOf("day").toISOString(),
            }
          );
        }
      };
      addWeekRanges();

      const addMonthQuarterYear = () => {
        const pushIf = (days, obj) => {
          if (this.historyLimitedAccess ? this.day >= days : true)
            list.push(obj);
        };
        pushIf(0, {
          id: "thisMonth",
          name: "This Month",
          start: this.$moment().startOf("month").toISOString(),
          end: this.$moment().endOf("month").toISOString(),
        });
        pushIf(0, {
          id: "thisMonthToDate",
          name: "This Month To Date",
          start: this.$moment().startOf("month").toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(60, {
          id: "lastMonth",
          name: "Last Month",
          start: this.$moment()
            .subtract(1, "month")
            .startOf("month")
            .toISOString(),
          end: this.$moment().subtract(1, "month").endOf("month").toISOString(),
        });
        pushIf(60, {
          id: "lastMonthToDate",
          name: "Last Month To Date",
          start: this.$moment()
            .subtract(1, "month")
            .startOf("month")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(182, {
          id: "thisQuarter",
          name: "This Quarter",
          start: this.$moment().startOf("quarter").toISOString(),
          end: this.$moment().endOf("quarter").toISOString(),
        });
        pushIf(182, {
          id: "thisQuarterToDate",
          name: "This Quarter To Date",
          start: this.$moment().startOf("quarter").toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(365, {
          id: "lastQuarter",
          name: "Last Quarter",
          start: this.$moment()
            .subtract(1, "quarter")
            .startOf("quarter")
            .toISOString(),
          end: this.$moment()
            .subtract(1, "quarter")
            .endOf("quarter")
            .toISOString(),
        });
        pushIf(365, {
          id: "lastQuarterToDate",
          name: "Last Quarter To Date",
          start: this.$moment()
            .subtract(1, "quarter")
            .startOf("quarter")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(365, {
          id: "thisYear",
          name: "This Year",
          start: this.$moment().startOf("year").toISOString(),
          end: this.$moment().endOf("year").toISOString(),
        });
        pushIf(365, {
          id: "thisYearToDate",
          name: "This Year To Date",
          start: this.$moment().startOf("year").toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(365, {
          id: "thisYearToLastMonth",
          name: "This Year To Last Month",
          start: this.$moment().startOf("year").toISOString(),
          end: this.$moment().subtract(1, "month").endOf("month").toISOString(),
        });
        pushIf(730, {
          id: "lastYear",
          name: "Last Year",
          start: this.$moment()
            .subtract(1, "year")
            .startOf("year")
            .toISOString(),
          end: this.$moment().subtract(1, "year").endOf("year").toISOString(),
        });
        pushIf(730, {
          id: "lastYearToDate",
          name: "Last Year To Date",
          start: this.$moment()
            .subtract(1, "year")
            .startOf("year")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(7, {
          id: "since7DaysAgo",
          name: "Since 7 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(7, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(15, {
          id: "since15DaysAgo",
          name: "Since 15 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(15, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(30, {
          id: "since30DaysAgo",
          name: "Since 30 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(30, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(60, {
          id: "since60DaysAgo",
          name: "Since 60 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(60, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(90, {
          id: "since90DaysAgo",
          name: "Since 90 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(90, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
        pushIf(365, {
          id: "since365DaysAgo",
          name: "Since 365 Days Ago",
          start: this.$moment()
            .startOf("days")
            .subtract(365, "days")
            .toISOString(),
          end: this.$moment().endOf("day").toISOString(),
        });
      };
      addMonthQuarterYear();

      return list;
    },

    startDate: {
      get() {
        return this.start.date ? this.start.date.format("YYYY-MM-DD") : null;
      },
      set(val) {
        if (!val) return (this.start.date = null);
        this.start.date = new this.$moment(val);
        if (this.start.time) {
          const time = this.start.time.split(":");
          this.start.date.set({ hour: time[0], minute: time[1] });
        }
      },
    },
    endDate: {
      get() {
        return this.end.date ? this.end.date.format("YYYY-MM-DD") : null;
      },
      set(val) {
        if (!val) return (this.end.date = null);
        this.end.date = new this.$moment(val);
        if (this.end.time) {
          const time = this.end.time.split(":");
          this.end.date.set({ hour: time[0], minute: time[1] });
        }
      },
    },

    startDateDisplay: {
      get() {
        return this.start.date
          ? this.start.date.format("MM/DD/YYYY") +
              (this.start.time ? ` ${this.start.time}` : "")
          : null;
      },
      set(val) {
        if (val) {
          const iso = new Date(val).toISOString();
          this.startDate = this.$moment(iso).format("YYYY-MM-DD");
        } else {
          this.startDate = null;
          this.start.time = null;
        }
        this.submit();
      },
    },
    endDateDisplay: {
      get() {
        return this.end.date
          ? this.end.date.format("MM/DD/YYYY") +
              (this.end.time ? ` ${this.end.time}` : "")
          : null;
      },
      set(val) {
        if (val) {
          const iso = new Date(val).toISOString();
          this.endDate = this.$moment(iso).format("YYYY-MM-DD");
        } else {
          this.endDate = null;
          this.end.time = null;
        }
        this.submit();
      },
    },

    startDateDisplayNormal: {
      get() {
        return this.start.date
          ? `${this.start.date.format("MM/DD/YYYY")} ${
              this.start.time
                ? this.$moment(
                    `${this.start.date.format("YYYY-MM-DD")} ${this.start.time}`
                  ).format("hh:mm a")
                : ""
            }`
          : null;
      },
      set() {
        this.start.time = null;
        this.start.date = null;
        return this.submit();
      },
    },
    endDateDisplayNormal: {
      get() {
        return this.end.date
          ? `${this.end.date.format("MM/DD/YYYY")} ${
              this.end.time
                ? this.$moment(
                    `${this.end.date.format("YYYY-MM-DD")} ${this.end.time}`
                  ).format("hh:mm a")
                : ""
            }`
          : null;
      },
      set() {
        this.end.time = null;
        this.end.date = null;
        return this.submit();
      },
    },
  },
  methods: {
    loadWeeks() {
      this.weeks = [
        {
          start: this.autoWeek(1).start,
          end: this.autoWeek(1).end,
          name: "One Week Ago",
        },
        {
          start: this.autoWeek(2).start,
          end: this.autoWeek(2).end,
          name: "Two Week Ago",
        },
        {
          start: this.autoWeek(3).start,
          end: this.autoWeek(3).end,
          name: "Three Week Ago",
        },
        {
          start: this.autoWeek(4).start,
          end: this.autoWeek(4).end,
          name: "Four Week Ago",
        },
      ];
    },
    loadDays() {
      this.days = [];
      for (let index = 1; index < 15; index++) {
        const d = this.autoDay(index);
        this.days.push({
          start: d.start,
          end: d.end,
          name: this.$moment(d.start).format("dddd"),
        });
      }
    },
    // getDayForHistory() {
    //   if (
    //     this.currentEmployee &&
    //     this.currentEmployee.EmployeeRole &&
    //     this.currentEmployee.EmployeeRole.EmployeeRolePermissions
    //   ) {
    //     const role = this.currentEmployee.EmployeeRole.EmployeeRolePermissions.filter(
    //       (p) => p.TerminalPermission.stringId === "HISTORYLIMITEDACCESS"
    //     )
    //     if (role.length > 0 && role[0].metadata && role[0].metadata.days) {
    //       this.day = parseInt(role[0].metadata.days)
    //     }
    //   }

    //   if (this.day === 0) {
    //     this.day = 1
    //   }
    // },
    customDate(obj) {
      this.start.date = this.$moment(obj.start);
      this.end.date = this.$moment(obj.end);
      this.submit();
      this.submitDate();
    },
    autoDay(number) {
      return {
        start: this.$moment()
          .subtract(number, "days")
          .startOf("days")
          .toISOString(),
        end: this.$moment().subtract(number, "days").endOf("day").toISOString(),
      };
    },
    autoWeek(number) {
      return {
        start: this.$moment()
          .subtract(number, "week")
          .startOf("week")
          .toISOString(),
        end: this.$moment()
          .subtract(number, "week")
          .endOf("week")
          .toISOString(),
      };
    },
    cancelCustomDate() {
      if (this.selected) {
        this.startDate = this.selected.start;
        this.endDate = this.selected.end;
      } else {
        this.start.date = null;
        this.end.date = null;
      }
      this.customLabel = false;
      this.submit();
      this.$emit("submit");
    },
    submitDate() {
      this.$emit("submit");
      this.cancel();
      this.customLabel = true; // show chip
    },
    cancel() {
      this.custom = !this.custom;
      this.$emit("customDateSelected");
    },
    customBox() {
      this.custom = !this.custom;
      if (this.$refs.selectDropdown) this.$refs.selectDropdown.blur();
    },
    submit() {
      let date = null;
      if (this.range) {
        date = {
          range: this.selected.id,
          start: this.start.date
            ? this.start.time
              ? this.start.date.toISOString()
              : this.start.date.startOf("day").toISOString()
            : null,
          end: this.end.date
            ? this.end.time
              ? this.end.date.toISOString()
              : this.end.date.endOf("day").toISOString()
            : null,
        };
      } else if (this.start.date) {
        date = this.start.time
          ? this.start.date.toISOString()
          : this.start.date.startOf("day").toISOString();
      }
      this.start.selectTime = false;
      this.end.selectTime = false;
      if (date) this.$emit("input", date);
    },
  },
  watch: {
    selected: {
      handler(val) {
        // choosing a preset hides the chip
        this.customLabel = false;
        this.startDate = val.start;
        this.endDate = val.end;
        this.submit();
        this.$emit("submit");
      },
      deep: true,
    },
    startDateDisplayNormal(val) {
      if (val === null) {
        this.end.date = null;
        this.end.time = null;
      }
    },
  },
};
</script>
